export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Object URLs are same-origin; crossOrigin keeps canvas untainted for
    // any http(s) source too.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for cropping.'));
    img.src = src;
  });
}

/**
 * Draw `pixelCrop` of `imageSrc` onto a canvas and return it as a Blob.
 * Destructive crop used by ImageCropDialog before upload.
 * @param imageSrc
 * @param pixelCrop
 * @param mimeType
 * @param quality
 */
export async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: PixelCrop,
  mimeType = 'image/jpeg',
  quality = 0.92,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(pixelCrop.width);
  canvas.height = Math.round(pixelCrop.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context for cropping.');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas is empty — could not create crop.'));
      },
      mimeType,
      quality,
    );
  });
}
